import { AsideInviteEarn } from './aside-invite-earn';
import { AsideNotification } from './aside-notification';
import {
  InteractCheckIcon,
  InteractDarkIcon,
  InteractExitIcon,
  InteractI18nIcon,
  InteractLeaderboardIcon,
  InteractLightIcon,
  InteractSettingsIcon,
  InteractSystemModeIcon,
  InteractUserIcon,
  ViewInfoIcon,
  MediaDiscordIcon,
  MediaMediumIcon,
  MediaTelegramIcon,
  MediaWebsiteIcon,
  MediaXIcon,
  ViewChevronDownIcon,
} from '@/client/assets';
import { SpanLoading } from '@/client/components/fetch-status/span-loading';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/client/components/ui/collapsible';
import { Sheet, SheetContent, SheetTitle } from '@/client/components/ui/sheet';
import {
  APP_THEME_LOCKED,
  DISCORD_LINK,
  HELP_LINK,
  PRIVACY_LINK,
  TELEGRAM_LINK,
  TERMS_OF_USE_LINK,
  MEDIUM_LINK,
  NOTIFICATIONS_ENABLED,
  TWITTER_LINK,
  WEBSITE_LINK,
} from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import useLocale from '@/client/hooks/use-locale';
import { useModeAnimation } from '@/client/hooks/use-mode-animate';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { ThemeMode, useTheme } from '@/client/hooks/use-theme';
import { api } from '@/client/utils/api';
import { keepOpenOnTransactionStatus } from '@/client/utils/transaction-status-target';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';

const mediaList = [
  {
    name: 'telegram',
    href: TELEGRAM_LINK,
    icon: MediaTelegramIcon,
  },
  {
    name: 'discord',
    href: DISCORD_LINK,
    icon: MediaDiscordIcon,
  },
  {
    name: 'twitter-x',
    href: TWITTER_LINK,
    icon: MediaXIcon,
  },
  {
    name: 'medium',
    href: MEDIUM_LINK,
    icon: MediaMediumIcon,
  },
  {
    name: 'website',
    href: WEBSITE_LINK,
    icon: MediaWebsiteIcon,
  },
];

const themes: {
  name: string;
  themeMode: ThemeMode;
  icon: React.FC<{ className?: string }>;
}[] = [
  {
    name: 'dark-mode',
    themeMode: 'dark',
    icon: InteractDarkIcon,
  },
  {
    name: 'light-mode',
    themeMode: 'light',
    icon: InteractLightIcon,
  },
  {
    name: 'system-mode',
    themeMode: 'auto',
    icon: InteractSystemModeIcon,
  },
];

type MoreSliderProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};
export const MoreSlider = ({ isOpen, setIsOpen }: MoreSliderProps) => {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { isAuthLoading, logout } = useAuth();
  const { data: userInfo } = useSignedUserInfo(api);
  const { locale, locales, switchLocale } = useLocale();
  const { themeMode, setThemeMode } = useTheme();
  const { ref, startAnimate } = useModeAnimation<HTMLDivElement>();
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        aria-describedby={undefined}
        onInteractOutside={keepOpenOnTransactionStatus}
        side="left"
        className="p-3 overflow-y-auto h-full flex flex-col border-foreground/10"
      >
        <VisuallyHidden>
          <SheetTitle>{t('more')}</SheetTitle>
        </VisuallyHidden>
        <div className="flex flex-col flex-1">
          {/* Notifications off (BON-249): no notification entry. */}
          {NOTIFICATIONS_ENABLED && <AsideNotification />}
          <button
            type="button"
            className="p-2 flex items-center p-3 space-x-2.5 hover:bg-foreground/4  rounded-3"
            onClick={() => {
              navigate('/leaderboard');
            }}
          >
            <InteractLeaderboardIcon className="size-5 fill-foreground" />
            <span className="font-medium text-sm  leading-5">{t('leaderboard')}</span>
          </button>
          <button
            type="button"
            className="p-2 flex items-center p-3 space-x-2.5 hover:bg-foreground/4  rounded-3"
            onClick={() => {
              navigate('/how-it-works');
              setIsOpen(false);
            }}
          >
            <ViewInfoIcon className="size-5 fill-foreground" />
            <span className="font-medium text-sm  leading-5">{t('how-it-works', 'How it works')}</span>
          </button>
          <AsideInviteEarn />
          {userInfo && (
            <button
              type="button"
              className="p-2 flex items-center p-3 space-x-2.5 hover:bg-foreground/4  rounded-3"
              onClick={() => {
                navigate(`/user/${userInfo.uid}`);
                setIsOpen(false);
              }}
            >
              <InteractUserIcon className="size-5 fill-foreground" />
              <span className="font-medium text-sm  leading-5">{t('profile')}</span>
            </button>
          )}
          <Collapsible className="w-full">
            <CollapsibleTrigger asChild>
              <button className="p-2 flex w-full items-center justify-between p-3 space-x-2.5 hover:bg-foreground/4 rounded-3">
                <span className="flex items-center space-x-2.5">
                  <InteractI18nIcon className="size-5 fill-foreground" />
                  <span className="font-medium text-sm  leading-5">{t('languages')}</span>
                </span>
                <ViewChevronDownIcon className="size-4 fill-foreground/40" />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="flex flex-col items-start">
              {locales.map(item => (
                <button
                  key={`switch-locale-item-${item.name}`}
                  onClick={() => switchLocale(item.lang)}
                  className="p-2 flex w-full items-center justify-between p-3  pl-12 space-x-2.5 hover:bg-foreground/4  rounded-3"
                >
                  <span className="font-medium text-sm leading-5">{item.name}</span>
                  {locale === item.lang && <InteractCheckIcon className="size-4 fill-foreground/40" />}
                </button>
              ))}
            </CollapsibleContent>
          </Collapsible>
          {/* theme (hidden while the tenant theme is locked) */}
          {!APP_THEME_LOCKED && (
            <Collapsible className="w-full">
              <CollapsibleTrigger asChild>
                <button className="p-2 flex w-full items-center justify-between p-3 space-x-2.5 hover:bg-foreground/4  rounded-3">
                  <span className="flex items-center space-x-2.5">
                    {themeMode === 'dark' && <InteractDarkIcon className="!size-5 fill-foreground" />}
                    {themeMode === 'light' && <InteractLightIcon className="!size-5 fill-foreground" />}
                    {themeMode === 'auto' && <InteractSystemModeIcon className="!size-5 fill-foreground" />}
                    <span className="font-medium text-sm  leading-5">
                      {themeMode === 'dark' ? t('dark-mode') : themeMode === 'light' ? t('light-mode') : t('system-mode')}
                    </span>
                  </span>
                  <ViewChevronDownIcon className="size-4 fill-foreground/40" />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent ref={ref} className="flex flex-col items-start">
                {themes.map(item => (
                  <button
                    key={`switch-theme-item-${item.themeMode}`}
                    onClick={() => startAnimate(() => setThemeMode(item.themeMode))}
                    className="p-2 flex w-full items-center justify-between p-3  pl-12 space-x-2.5 hover:bg-foreground/4  rounded-3"
                  >
                    <span className="flex-1 flex items-center space-x-2.5">
                      <item.icon className="!size-5 fill-foreground" />
                      <span className="font-medium text-sm leading-5">{t(item.name)}</span>
                    </span>
                    {themeMode === item.themeMode && <InteractCheckIcon className="size-4 fill-foreground/40" />}
                  </button>
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
          {/* settings */}
          {userInfo && (
            <button
              className="p-2 flex  items-center p-3 space-x-2.5 hover:bg-foreground/4  rounded-3"
              onClick={() => {
                navigate(`/user/${userInfo.uid}/settings`);
                setIsOpen(false);
              }}
            >
              <InteractSettingsIcon className="!size-5 fill-foreground" />
              <span className="font-medium text-sm leading-5">{t('settings')}</span>
            </button>
          )}
          {/* logout */}
          {userInfo && (
            <button
              className="p-2 flex  items-center p-3 space-x-2.5 hover:bg-foreground/4  rounded-3"
              disabled={isAuthLoading}
              onClick={async () => {
                await logout();
              }}
            >
              <InteractExitIcon className="!size-5 fill-foreground" />
              {isAuthLoading && <SpanLoading />}
              <span className="font-medium text-sm leading-5">{t('logout')}</span>
            </button>
          )}
        </div>
        <div className="w-full px-3 space-y-3">
          {mediaList.some(item => item.href) && (
            <div className="py-3 text-sm text-foreground/70 flex items-center justify-between">
              {mediaList
                .filter(item => item.href)
                .map(item => (
                  <a
                    key={`media-item-${item.name}`}
                    href={item.href}
                    aria-label={item.name}
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 rounded-2 bg-foreground/4"
                  >
                    <item.icon aria-hidden="true" className="size-5 fill-current" />
                  </a>
                ))}
            </div>
          )}
          <div className="text-xs text-foreground/40 space-x-4">
            {HELP_LINK && (
              <a href={HELP_LINK} target="_blank" rel="noreferrer">
                {t('help')}
              </a>
            )}
            <a href={TERMS_OF_USE_LINK} target="_blank" rel="noreferrer">
              {t('terms-of-use')}
            </a>
            <a href={PRIVACY_LINK} target="_blank" rel="noreferrer">
              {t('privacy-policy')}
            </a>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
